// Phase 3.3 — Procurement add/edit form.
//
// Replaces the antd <Drawer> + <Form> in procurement.tsx with a shadcn <Sheet> +
// FieldRow layout. Presentation only: the form model stays the parent's
// `procurementFormData`, submit still calls the screen's
// `createOrUpdateProcurementApi` (same dispatched payload). The antd Form `rules`
// (Category / Sub-Category / Count required) are ported one-to-one into a local
// `validate()` gate. Sub-category options are derived here from the selected
// category (the legacy module-level `subCategoryList` is gone).
import * as React from "react";
import { Eye, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "ui/dialog";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { FieldRow } from "../../shared/form-section";
import { Combobox } from "ui/combobox";
import { FileDropzone } from "../../shared/file-dropzone";
import { deleteFileFromS3 } from "../SubComponents/awsUpload";
import AppConstant from "../../Globals/AppConstants";

// AWS "type" location code for procurement images — see checkType() in
// awsUpload.ts (case 6 -> "procurement/images/").
const PROCUREMENT_IMAGE_TYPE = 6;
const PROCUREMENT_IMG_BASE = `${process.env.REACT_APP_AWS_CLOUD_FRONT_URL}/${process.env.REACT_APP_IMAGE_DESTINATION_BUCKET}procurement/images/`;

export interface ProcurementFormSheetProps {
  open: boolean;
  onClose: () => void;
  categoryList: any[];
  formData: any;
  /** Category change: set categoryId + clear subCategoryId (legacy filter reset). */
  onCategoryChange: (categoryId: any) => void;
  onChange: (patch: any) => void;
  /** Fires only when validation passes; runs the screen's createOrUpdateProcurementApi. */
  onSubmit: () => void;
}

type Errors = { category?: string; subCategory?: string; count?: string };

export function ProcurementFormSheet({
  open,
  onClose,
  categoryList,
  formData,
  onCategoryChange,
  onChange,
  onSubmit,
}: ProcurementFormSheetProps) {
  const [errors, setErrors] = React.useState<Errors>({});
  const [previewOpen, setPreviewOpen] = React.useState(false);

  // Sub-categories of the currently-selected category.
  const subCategories = React.useMemo(() => {
    const cat = (categoryList || []).find((c: any) => c.categoryId == formData?.categoryId);
    return (cat?.subCategories || []).map((s: any) => ({
      subCategoryId: s?.subCategoryId,
      subCategoryName: s?.subCategoryName,
    }));
  }, [categoryList, formData?.categoryId]);

  const categoryComboOptions = React.useMemo(
    () => (categoryList || []).map((c: any) => ({ value: String(c?.categoryId), label: c?.categoryName })),
    [categoryList]
  );

  const subCategoryComboOptions = React.useMemo(
    () => subCategories.map((s: any) => ({ value: String(s?.subCategoryId), label: s?.subCategoryName })),
    [subCategories]
  );

  // Ported one-to-one from the antd Form.Item `rules`.
  const validate = (): boolean => {
    const next: Errors = {};
    if (!formData?.categoryId) next.category = "Category is required!";
    if (!formData?.subCategoryId) next.subCategory = "Sub-Category is required!";
    if (!formData?.count && formData?.count !== 0) next.count = "Count is required!";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSubmit();
  };

  const close = () => {
    setErrors({});
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle>Procurement</SheetTitle>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
          <FieldRow label="Category" required error={errors.category}>
            <Combobox
              options={categoryComboOptions}
              value={formData?.categoryId != null ? String(formData.categoryId) : undefined}
              onValueChange={(v) => { onCategoryChange(v); setErrors((e) => ({ ...e, category: undefined })); }}
              placeholder="Select category"
              searchPlaceholder="Search category..."
            />
          </FieldRow>

          <FieldRow label="Sub-Category" required error={errors.subCategory}>
            <Combobox
              options={subCategoryComboOptions}
              value={formData?.subCategoryId != null ? String(formData.subCategoryId) : undefined}
              disabled={formData?.categoryId == null}
              onValueChange={(v) => { onChange({ subCategoryId: v }); setErrors((e) => ({ ...e, subCategory: undefined })); }}
              placeholder="Select sub-category"
              searchPlaceholder="Search sub-category..."
            />
          </FieldRow>

          <FieldRow label="Description">
            <textarea
              className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={formData?.description ?? ""}
              onChange={(e) => onChange({ description: e.target.value })}
            />
          </FieldRow>

          <FieldRow label="Image">
            {formData?.imageFileUuid ? (
              <div className="relative flex items-center gap-3 rounded-xl border p-3">
                <img
                  src={`${PROCUREMENT_IMG_BASE}${formData.imageFileUuid}`}
                  alt="Procurement"
                  className="h-12 w-12 rounded-lg object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">Image uploaded</p>
                </div>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  aria-label="View image"
                  className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    deleteFileFromS3(formData.imageFileUuid, PROCUREMENT_IMAGE_TYPE);
                    onChange({ imageFileUuid: null });
                  }}
                  aria-label="Remove image"
                  className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <FileDropzone
                type={PROCUREMENT_IMAGE_TYPE}
                onUploaded={(file) => onChange({ imageFileUuid: file.fileUuid })}
                hint="Single image only"
              />
            )}
          </FieldRow>

          <FieldRow label="Count" required error={errors.count}>
            <Input
              value={formData?.count ?? ""}
              onChange={(e) => { onChange({ count: e.target.value }); setErrors((er) => ({ ...er, count: undefined })); }}
            />
          </FieldRow>

          <div className="mt-auto flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={close}>{AppConstant?.cancel}</Button>
            <Button type="submit">{AppConstant?.save}</Button>
          </div>
        </form>
      </SheetContent>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogTitle className="sr-only">Procurement Image</DialogTitle>
          {formData?.imageFileUuid && (
            <img
              src={`${PROCUREMENT_IMG_BASE}${formData.imageFileUuid}`}
              alt="Procurement"
              className="max-h-[80vh] w-full rounded-lg object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}

export default ProcurementFormSheet;
